import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { APP_VERSION } from "../lib/version";
import { ApiError, createRoom, getRoom, isValidCode, normalizeCode } from "../net/api";
import { clearSeat, listSavedSeats } from "../state/local";

export function HomeScreen() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(listSavedSeats);

  // Rooms close a while after their last game. Drop any this phone still remembers that the
  // server no longer has; if the server cannot be reached, keep them and check next time.
  useEffect(() => {
    let cancelled = false;
    for (const { code: c } of listSavedSeats()) {
      getRoom(c)
        .then((info) => {
          if (info !== null) return;
          clearSeat(c);
          if (!cancelled) setSaved((list) => list.filter((s) => s.code !== c));
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const newCode = await createRoom();
      navigate(`/room/${newCode}`);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Could not reach the server. Check your connection.",
      );
    } finally {
      setCreating(false);
    }
  };

  const normalized = normalizeCode(code);
  const join = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidCode(normalized)) navigate(`/room/${normalized}`);
  };

  return (
    <main className="screen screen-center" data-screen="home">
      <div className="stack" style={{ marginBottom: 24 }}>
        <div className="home-mark" aria-hidden>
          ◷
        </div>
        <h1>TowerClock</h1>
        <p className="muted">
          A social deduction game for the table. The Storyteller lives in everyone&apos;s phone.
        </p>
      </div>

      <form className="card stack" onSubmit={join}>
        <div className="field">
          <label htmlFor="code">Join a room</label>
          <input
            id="code"
            className="input input-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CODE"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={6}
            inputMode="text"
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={!isValidCode(normalized)}>
          Join
        </button>
      </form>

      <div className="row" style={{ justifyContent: "center" }}>
        <span className="faint small">or</span>
      </div>

      <button className="btn" onClick={create} disabled={creating}>
        {creating ? "Creating…" : "Create a new room"}
      </button>
      {error && <p className="error-text">{error}</p>}

      {saved.length > 0 && (
        <div className="stack-sm" style={{ marginTop: 16 }}>
          <span className="eyebrow">Continue</span>
          {saved.map(({ code: c, seat }) => (
            <button key={c} className="btn btn-ghost" onClick={() => navigate(`/room/${c}`)}>
              <span className="mono">{c}</span>
              {seat.name && <span className="muted">as {seat.name}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="stack-sm" style={{ marginTop: 24 }}>
        <p className="tiny faint">5 to 15 players, one phone each, sitting in a circle.</p>
        <p className="small">
          <Link to="/how-to-play">How to play</Link> · <Link to="/roles">Characters</Link> ·{" "}
          <Link to="/changelog">Changelog</Link>
        </p>
        <p className="tiny faint">Version {APP_VERSION}</p>
      </div>
    </main>
  );
}
